import type { i18n } from "i18next"
import { DateTime, Interval } from "luxon"

import type { IDateFormat } from "../models/date/date"
import { EMPTY_DATA_PLACEHOLDER } from "./constants"
import { getDisplayValue } from "./value"

/** POSIX aliases that read more clearly than canonical IANA names. */
const IANA_TO_POSIX_TIMEZONE: Record<string, string> = {
  "America/Adak": "US/Aleutian",
  "America/Anchorage": "US/Alaska",
  "America/Chicago": "US/Central",
  "America/Denver": "US/Mountain",
  "America/Detroit": "US/Michigan",
  "America/Indiana/Indianapolis": "US/East-Indiana",
  "America/Indiana/Knox": "US/Indiana-Starke",
  "America/Indianapolis": "US/East-Indiana",
  "America/Los_Angeles": "US/Pacific",
  "America/New_York": "US/Eastern",
  "America/Phoenix": "US/Arizona",
  "Pacific/Honolulu": "US/Hawaii",
  "Pacific/Pago_Pago": "US/Samoa",
}

export const getYearMonthDayISOString = (
  year: number | string,
  month: number | string,
  day: number | string = 1
): string =>
  `${year}-${Number(month).toString().padStart(2, "0")}-${Number(day)
    .toString()
    .padStart(2, "0")}`

export const removeTimeFromISODateString = (isoString: string | null): string =>
  isoString?.split("T")[0] ?? ""

/** Clamps a calendar date to optional min/max bounds. */
export const clampDate = (
  value: DateTime,
  minDate: DateTime | null | undefined,
  maxDate: DateTime | null | undefined
): DateTime => {
  let next = value.startOf("day")
  if (minDate?.isValid) {
    next = DateTime.max(next, minDate.startOf("day"))
  }
  if (maxDate?.isValid) {
    next = DateTime.min(next, maxDate.startOf("day"))
  }
  return next
}

/** Last calendar day of a half-open interval. */
export const inclusiveIntervalEnd = (range: Interval): DateTime | null =>
  range.end?.minus({ days: 1 }).startOf("day") ?? null

/** Half-open interval whose end is the last included day. */
export const toInclusiveInterval = (range: Interval): Interval => {
  const inclusiveEnd = inclusiveIntervalEnd(range)
  if (!range.start?.isValid || !inclusiveEnd?.isValid) {
    return range
  }

  return Interval.fromDateTimes(range.start.startOf("day"), inclusiveEnd)
}

/** Half-open interval covering each included calendar day. */
export const intervalFromInclusiveDates = (
  start: DateTime,
  end: DateTime
): Interval =>
  Interval.fromDateTimes(
    start.startOf("day"),
    end.startOf("day").plus({ days: 1 })
  )

interface CompleteInclusivePickerRange {
  end: DateTime
  start: DateTime
}

interface InclusivePickerRange {
  end: DateTime | null
  start: DateTime | null
}

export const isCompleteInclusivePickerRange = (
  range: InclusivePickerRange
): range is CompleteInclusivePickerRange =>
  Boolean(range.start?.isValid && range.end?.isValid)

interface PickerBounds {
  maxDate: DateTime
  minDate: DateTime
}

interface PickerBoundsForPartialRangeOptions {
  end: DateTime | null
  maxDate: DateTime
  minDate: DateTime
  start: DateTime | null
}

/** Keeps a half-entered range inside the bounds the user can still pick. */
export const pickerBoundsForPartialRange = ({
  end,
  maxDate,
  minDate,
  start,
}: PickerBoundsForPartialRangeOptions): PickerBounds => {
  if (start?.isValid && !end?.isValid) {
    return {
      maxDate,
      minDate: DateTime.max(minDate, start.startOf("day")),
    }
  }

  if (end?.isValid && !start?.isValid) {
    return {
      maxDate: DateTime.min(maxDate, end.startOf("day")),
      minDate,
    }
  }

  return { maxDate, minDate }
}

/** Half-open interval ending the midnight after the last included day. */
export const toExclusiveInterval = (range: Interval): Interval => {
  if (!range.start?.isValid || !range.end?.isValid) {
    return range
  }

  return intervalFromInclusiveDates(range.start, range.end)
}

/**
 * Formats and internationalizes the provided date
 *
 * @param i18nService - The internationalization service
 * @param props.value - The date
 * @param props.format - The format configuration for the date
 * @returns - Internationalized date in requested format
 * @example
 * translateDate(i18n,
        date,
        {
          day: "numeric",
          month: "numeric",
      })) // day and month ordered with separator appropriately for internationalization
 * translateDate(i18n, date)) // day, month and year ordered with separator appropriately for internationalization
 */

export const translateDate = (
  i18nService: i18n,
  value: DateTime,
  format?: IDateFormat | null
) =>
  // Ensure we're always showing the date in the date's timezone, not the client's timezone
  Intl.DateTimeFormat(i18nService.language, {
    ...format,
    timeZone: value.zoneName ?? undefined,
  }).format(value.toJSDate())

export const formatTimezone = (timeZone: string | null | undefined): string => {
  const displayValue = getDisplayValue(timeZone)
  if (displayValue === EMPTY_DATA_PLACEHOLDER) {
    return displayValue
  }

  const zoned = DateTime.utc().setZone(displayValue)
  if (!zoned.isValid || zoned.zoneName == null) {
    return displayValue
  }

  const zoneName = zoned.zoneName
  if (zoneName.startsWith("US/")) {
    return zoneName
  }

  return IANA_TO_POSIX_TIMEZONE[zoneName] ?? zoneName
}

export const PERIOD_DATE_FORMAT: IDateFormat = {
  day: "numeric",
  month: "numeric",
  year: "numeric",
}

export const PERIOD_DATE_TIME_FORMAT: IDateFormat = {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "numeric",
  year: "numeric",
}
